import React, { useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ArrowRight, BookOpenCheck, LockKeyhole, Search, Target } from "lucide-react";
import StudyShell from "@/components/study/StudyShell";
import StudyRunner from "@/components/study/StudyRunner";
import { getStudyQuestions } from "@/lib/api";
import { STUDY } from "@/constants/testIds";
import { useStudyAuth } from "@/context/StudyAuth";

const PORTION_LABELS = {
  state: "Illinois State",
  national: "National Concepts",
  math: "Real Estate Math",
};

const PORTION_ORDER = ["state", "national", "math"];

const normalize = (value) => String(value || "").toLowerCase().trim();

function buildBankSummary(questions) {
  const sections = PORTION_ORDER.map((portion) => ({
    portion,
    label: PORTION_LABELS[portion],
    count: questions.filter((q) => q.portion === portion).length,
  }));

  const topicMap = new Map();
  questions.forEach((q) => {
    const topic = q.topic || "General";
    const key = `${q.portion}:${topic}`;
    const entry = topicMap.get(key) || {
      topic,
      portion: q.portion,
      label: PORTION_LABELS[q.portion] || "Mixed",
      count: 0,
      difficulty: {},
    };
    entry.count += 1;
    entry.difficulty[q.difficulty || "medium"] =
      (entry.difficulty[q.difficulty || "medium"] || 0) + 1;
    topicMap.set(key, entry);
  });

  const topics = Array.from(topicMap.values()).sort((a, b) => {
    const portionDelta = PORTION_ORDER.indexOf(a.portion) - PORTION_ORDER.indexOf(b.portion);
    if (portionDelta !== 0) return portionDelta;
    if (b.count !== a.count) return b.count - a.count;
    return a.topic.localeCompare(b.topic);
  });

  return { sections, topics };
}

function StatCard({ label, value, children }) {
  return (
    <div className="rounded-2xl border border-[color:rgba(10,27,51,0.12)] bg-white p-5 shadow-[0_10px_30px_rgba(7,20,39,0.05)]">
      <p className="text-xs font-semibold uppercase tracking-wide text-[color:rgba(11,18,32,0.5)]">
        {label}
      </p>
      <p className="mt-1 text-3xl font-bold text-[var(--ibp-ink)]">{value}</p>
      {children}
    </div>
  );
}

export default function StudyQuestionBank() {
  const { hasFullAccess } = useStudyAuth();
  const { search } = useLocation();
  const [active, setActive] = useState(null);
  const [query, setQuery] = useState("");

  const presetTopic = useMemo(
    () => new URLSearchParams(search).get("topic") || "",
    [search]
  );

  const { data: questions, isLoading } = useQuery({
    queryKey: ["study-question-bank"],
    queryFn: () => getStudyQuestions({ mode: "all" }),
  });

  const list = useMemo(() => questions || [], [questions]);
  const { sections, topics } = useMemo(() => buildBankSummary(list), [list]);

  useEffect(() => {
    if (!presetTopic || active || topics.length === 0) return;
    const found = topics.find((t) => t.topic === presetTopic);
    if (found) {
      setActive({ mode: "topic", topic: found.topic, label: found.topic });
    }
  }, [presetTopic, active, topics]);

  const filteredTopics = useMemo(() => {
    const q = normalize(query);
    if (!q) return topics;
    return topics.filter(
      (t) =>
        normalize(t.topic).includes(q) ||
        normalize(t.label).includes(q) ||
        normalize(t.portion).includes(q)
    );
  }, [topics, query]);

  return (
    <StudyShell testId={STUDY.questionBankPage}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-[var(--ibp-gold-600,#b8860b)]">
            {hasFullAccess ? "Full question bank" : "Free practice library"}
          </p>
          <h1 className="mt-1 text-2xl font-bold text-[var(--ibp-ink)]">Question Bank</h1>
          <p className="mt-1 max-w-2xl text-sm leading-6 text-[color:rgba(11,18,32,0.65)]">
            {hasFullAccess
              ? "Study the complete active bank by section or topic. Each set uses the same answer-and-explanation runner so you can build coverage without losing your progress."
              : "Practice 25 questions across Illinois, national concepts, and math. Every answer includes the same explanations and source references as full access."}
          </p>
        </div>
        {active && (
          <button
            type="button"
            onClick={() => setActive(null)}
            className="rounded-lg px-3 py-1.5 text-sm font-semibold text-[var(--ibp-navy-950)] hover:bg-[color:rgba(10,27,51,0.06)]"
          >
            Back to bank
          </button>
        )}
      </div>

      {!hasFullAccess ? (
        <div className="mt-5 flex flex-col gap-3 border-l-4 border-l-[var(--ibp-gold-500)] bg-white px-5 py-4 sm:flex-row sm:items-center sm:justify-between">
          <p className="text-sm leading-6 text-[color:rgba(11,18,32,0.68)]">
            You are viewing the free 25-question set. Your results stay saved if you upgrade.
          </p>
          <Link to="/product" className="inline-flex shrink-0 items-center gap-1.5 text-sm font-semibold text-[#0F3D8A] hover:underline">
            <LockKeyhole className="h-4 w-4" /> Unlock the full library
          </Link>
        </div>
      ) : null}

      {active ? (
        <div className="mt-6">
          <div className="mb-4 rounded-2xl border border-[var(--ibp-gold-500)] bg-[color:rgba(214,162,30,0.08)] p-4">
            <p className="text-xs font-semibold uppercase tracking-wide text-[color:rgba(11,18,32,0.55)]">
              Active set
            </p>
            <h2 className="mt-1 text-lg font-bold text-[var(--ibp-ink)]">{active.label}</h2>
          </div>
          <StudyRunner
            mode={active.mode}
            portion={active.portion}
            topic={active.topic}
            emptyText="No active questions in this set right now."
          />
        </div>
      ) : isLoading ? (
        <p className="mt-8 text-sm text-[color:rgba(11,18,32,0.6)]">Loading question bank...</p>
      ) : (
        <>
          <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-4">
            <StatCard label={hasFullAccess ? "Active bank" : "Free set"} value={list.length}>
              <button
                type="button"
                data-testid={STUDY.questionBankStart}
                onClick={() => setActive({ mode: "all", label: "All active questions" })}
                className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-[var(--ibp-navy-950)] hover:underline"
              >
                Start all <ArrowRight className="h-4 w-4" />
              </button>
            </StatCard>
            {sections.map((s) => (
              <StatCard key={s.portion} label={s.label} value={s.count}>
                <button
                  type="button"
                  onClick={() =>
                    setActive({ mode: "section", portion: s.portion, label: s.label })
                  }
                  className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-[var(--ibp-navy-950)] hover:underline"
                >
                  Practice section <ArrowRight className="h-4 w-4" />
                </button>
              </StatCard>
            ))}
          </div>

          <div className="mt-8 rounded-2xl border border-[color:rgba(10,27,51,0.12)] bg-white p-5 shadow-[0_10px_30px_rgba(7,20,39,0.05)]">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <h2 className="flex items-center gap-2 text-base font-semibold text-[var(--ibp-ink)]">
                  <BookOpenCheck className="h-4 w-4" /> Topic index
                </h2>
                <p className="mt-1 text-sm text-[color:rgba(11,18,32,0.6)]">
                  Choose one topic for focused practice.
                </p>
              </div>
              <label className="relative block w-full sm:w-72">
                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[color:rgba(11,18,32,0.45)]" />
                <input
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Search topics"
                  className="h-10 w-full rounded-xl border border-[color:rgba(10,27,51,0.15)] bg-white pl-9 pr-3 text-sm outline-none focus:border-[var(--ibp-navy-950)]"
                />
              </label>
            </div>

            <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
              {filteredTopics.map((t) => (
                <button
                  key={`${t.portion}:${t.topic}`}
                  type="button"
                  data-testid={STUDY.questionBankTopic}
                  onClick={() => setActive({ mode: "topic", topic: t.topic, label: t.topic })}
                  className="rounded-xl border border-[color:rgba(10,27,51,0.12)] bg-[var(--ibp-offwhite)] px-4 py-3 text-left transition-colors hover:border-[var(--ibp-navy-950)] hover:bg-white"
                >
                  <span className="flex items-start justify-between gap-3">
                    <span>
                      <span className="block text-sm font-semibold text-[var(--ibp-ink)]">
                        {t.topic}
                      </span>
                      <span className="mt-1 inline-flex items-center gap-1.5 text-xs font-medium text-[color:rgba(11,18,32,0.6)]">
                        <Target className="h-3.5 w-3.5" /> {t.label}
                      </span>
                    </span>
                    <span className="rounded-full bg-white px-2.5 py-1 text-xs font-bold text-[var(--ibp-ink)]">
                      {t.count}
                    </span>
                  </span>
                </button>
              ))}
            </div>
          </div>
        </>
      )}
    </StudyShell>
  );
}
